import {
  Heading,
  styled,
  Text,
  PrimaryButton,
  Link as SourcefulLink,
} from "@sourceful/shared-components";
import Link from "next/link";
import toRem from "@styles/postprocess/conversion/to-rem";
import { getContentGridCol, getContentGridRow } from "./helpers";
import { BannerInstance, SanityTextAlign } from "@sourceful/shared-types";

const BannerContentWrapper = styled("div", {
  height: "100%",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  h3: {
    fontSize: "$h4",
  },
});

const InnerTextContent = styled("div", {
  display: "grid",
  gridGap: "$inline_m1",
});

const ButtonGroup = styled("div", {
  display: "flex",
  flexShrink: 0,
  flexWrap: "wrap",
  a: {
    margin: toRem(8),
  },
  "a:last-child": {
    marginRight: 0,
  },
});

interface BannerContentProps {
  bannerInstance: BannerInstance;
  hasAdequateSpace?: boolean;
}

const getInnerTextContentPadding = (textAlign: SanityTextAlign): object => {
  if (textAlign === "left") {
    return {
      paddingInlineStart: "0",
      paddingInlineEnd: "clamp(10px, 10%, 80px)",
    };
  }

  if (textAlign === "right") {
    return {
      paddingInlineStart: "clamp(10px, 10%, 80px)",
      paddingInlineEnd: "0",
    };
  }

  return {
    paddingInline: "clamp(10px, 10%, 80px)",
  };
};

const BannerContent = ({ bannerInstance, hasAdequateSpace }: BannerContentProps) => {
  const {
    textAlign,
    banner,
    layout,
    textColor,
    showDescription,
    showLinkCTA,
    showSecondaryCTA,
    showPrimaryCTA,
  } = bannerInstance;
  const { title, description, primaryCTA, secondaryCTA, linkCTA } = banner;

  return (
    <BannerContentWrapper
      css={{
        textAlign,
        gridColumn: getContentGridCol(layout, hasAdequateSpace),
        gridRow: getContentGridRow(layout, hasAdequateSpace),
      }}
    >
      <InnerTextContent
        css={{
          paddingBlock: "clamp(10px, 10%, 80px)",
          ...getInnerTextContentPadding(textAlign),
          h3: {
            color: textColor?.value || "$neutral_solid_100",
          },
          "& p": {
            color: textColor?.value || "$neutral_solid_100",
          },
        }}
      >
        <Heading level={3}>{title}</Heading>
        {description && showDescription && <Text level={3}>{description}</Text>}

        {primaryCTA && showPrimaryCTA && (
          <ButtonGroup
            css={{
              a: {
                textAlign: "center",
              },
              justifyContent:
                textAlign === "left" ? "flex-start" : textAlign === "right" ? "flex-end" : "center",
            }}
          >
            <Link href={primaryCTA.url} legacyBehavior>
              <PrimaryButton as="a" href={primaryCTA.url} compact>
                {primaryCTA.title}
              </PrimaryButton>
            </Link>
            {secondaryCTA && showSecondaryCTA && (
              <Link href={secondaryCTA.url} legacyBehavior>
                <PrimaryButton appearance="light" as="a" href={secondaryCTA.url} compact>
                  {secondaryCTA.title}
                </PrimaryButton>
              </Link>
            )}
            {linkCTA && showLinkCTA && (
              <Link href={linkCTA.url} legacyBehavior>
                <SourcefulLink href={linkCTA.url}>{linkCTA.url}</SourcefulLink>
              </Link>
            )}
          </ButtonGroup>
        )}
      </InnerTextContent>
    </BannerContentWrapper>
  );
};

export default BannerContent;
